import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { ContainerCard } from "../../../components/cards/containerCard";
import { InformationCard } from "../../../components/cards/infoCard";
import { Icon } from "../../../components/icons/icon";
import { ConfirmationModal } from "../../../components/modals/app_modals/confirmationModal";
import { ProgressTrail } from "../../../components/progress/progressTrail";
import { useModalControl } from "../../../customHooks/useModalControl";
import {
  formatToCurrency,
  parseStringToNumber,
} from "../../../helperFunctions";
import {
  useGetMembershipProfileQuery,
  useGetSingleCooperativeDetailsQuery,
  useJoinCooperativeMutation,
} from "../../../redux/features/cooperative/cooperative.api";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../utils/constants";
import { MembershipForm } from "./components/membershipForm";
import { PayMembershipFee } from "./components/payFeeComponent";
import { ProfileInformation } from "./components/profileInformation";

type Steps = "Personal Information" | "A little bit more" | "Membership Fee";

export const JoinCooperative = () => {
  const [cooperativeConfig, setCooperativeConfig] = useState<{
    hasForm: boolean;
    hasFee: boolean;
  }>({
    hasForm: false,
    hasFee: false,
  });
  const [step, setStep] = useState<Steps>("Personal Information");
  const [steps, setSteps] = useState<Steps[]>([]);

  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const { modal, handleModalClose, handleModalOpen, modalOpen } =
    useModalControl();

  const navigate = useNavigate();

  const {
    data: singleCooperative,
    isFetching: fetchingCooperativeDetails,
    isError: cooperativeDetailsFetchError,
  } = useGetSingleCooperativeDetailsQuery(
    {
      id: id!,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    }
  );

  const {
    isSuccess: memberProfileSuccess,
    isFetching: fetchingMemberProfile,
    data: memberProfile,
  } = useGetMembershipProfileQuery(
    { coop: id!, Permission: false },
    {
      skip: !id,
    }
  );

  const [joinCooperative, { isLoading: joiningCooperative }] =
    useJoinCooperativeMutation();

  useEffect(() => {
    if (cooperativeDetailsFetchError) {
      navigate("/app/overview", { replace: true });
    }
    // eslint-disable-next-line
  }, [cooperativeDetailsFetchError]);

  useEffect(() => {
    const membershipFee =
      parseStringToNumber(
        singleCooperative?.data?.formdetail?.form?.Fee ?? 0
      ) ?? 0;
    const membershipFormLength =
      singleCooperative?.data?.formdetail?.Field?.length ?? 0;

    let tempSteps: Steps[] = [];
    let tempCooperativeConfig = {
      hasFee: false,
      hasForm: false,
    };

    if (membershipFee === 0 && membershipFormLength === 0) {
      tempSteps = ["Personal Information"];
      tempCooperativeConfig = {
        hasFee: false,
        hasForm: false,
      };
    } else if (membershipFee === 0 && membershipFormLength > 0) {
      tempSteps = ["Personal Information", "A little bit more"];
      tempCooperativeConfig = {
        hasFee: false,
        hasForm: true,
      };
    } else if (membershipFee > 0 && membershipFormLength === 0) {
      tempSteps = ["Personal Information", "Membership Fee"];
      tempCooperativeConfig = {
        hasFee: true,
        hasForm: false,
      };
    } else if (membershipFee > 0 && membershipFormLength > 0) {
      tempSteps = [
        "Personal Information",
        "A little bit more",
        "Membership Fee",
      ];
      tempCooperativeConfig = {
        hasFee: true,
        hasForm: true,
      };
    }

    // Take out fee payment step commpletey if specified
    const isPaymentRequired = !(
      (searchParams.get("payment") ?? "true").toLowerCase() === "false"
    );

    if (!isPaymentRequired) {
      setSteps(tempSteps.filter((v) => v !== "Membership Fee"));
      setCooperativeConfig({
        ...tempCooperativeConfig,
        hasFee: false,
      });
    } else {
      setSteps(tempSteps);
      setCooperativeConfig({
        ...tempCooperativeConfig,
      });
    }
  }, [singleCooperative, searchParams]);

  useEffect(() => {
    if (memberProfileSuccess && memberProfile?.data?.hasPaid) {
      navigate("/app/my-cooperatives", { replace: true });
    } else if (
      memberProfileSuccess &&
      !memberProfile?.data?.hasPaid &&
      cooperativeConfig.hasFee &&
      steps.includes("Membership Fee")
    ) {
      setStep("Membership Fee");
    } else if (!id) {
      navigate("/app/overview", { replace: true });
    }
    // eslint-disable-next-line
  }, [id, memberProfileSuccess, memberProfile, cooperativeConfig, steps]);

  return (
    <>
      <ContainerCard
        className="
        w-full max-w-[600px] min-h-full h-fit mx-auto
        py-10 px-5 md:px-6 flex flex-col
        "
      >
        <div className="flex items-center justify-between gap-8">
          <h1 className="text-base md:text-[20px] font-bold">
            Membership Form
          </h1>

          <button
            className="
            text-white size-[20px] md:size-[28px] bg-pry-dark-green-300
            rounded-full w-fit
            "
            onClick={() => {
              handleModalOpen("closeMembershipFormModal");
            }}
          >
            <Icon
              svg="cancel-icon"
              width={20}
              height={20}
              className="mx-auto my-auto"
            />
          </button>
        </div>

        {fetchingCooperativeDetails ? (
          <div className="flex flex-col gap-2 mt-2">
            <Skeleton
              width={"100%"}
              height={"20px"}
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
            />

            <Skeleton
              width={"30%"}
              height={"20px"}
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
            />
          </div>
        ) : (
          <p className="w-full text-sm md:text-base text-pry-dark-green-300/60 mt-2">
            To join{" "}
            <span className="text-pry-green-200 font-medium">
              {singleCooperative?.data?.CoopName}
            </span>
            , kindly review your information
            {cooperativeConfig.hasForm ? (
              <>
                , {cooperativeConfig.hasFee ? "" : "and"} fill out the
                membership form
              </>
            ) : undefined}
            {cooperativeConfig.hasFee ? (
              <>
                , and pay a registration fee of{" "}
                <span className="text-pry-green-200 font-medium">
                  {formatToCurrency(
                    singleCooperative?.data?.formdetail?.form?.Fee!,
                    { decimalPlaces: 2, currency: "NGN" }
                  )}
                </span>
              </>
            ) : undefined}
          </p>
        )}

        {/* Edit profile information card  */}
        {step === "Personal Information" && !fetchingCooperativeDetails ? (
          <InformationCard
            info="Click the button to edit your profile"
            variant="purple"
            size="mini"
            hasIcon={true}
            className="!items-center mt-3"
            actionElement={
              <Button
                text="Edit profile"
                onClick={() => {
                  navigate("/app/settings/profile/edit");
                }}
                type="button"
                className="!w-fit !text-xs"
                loading={false}
                disabled={false}
                variation="primary"
                size="small"
              />
            }
          />
        ) : undefined}

        {/* Progress Trail  */}
        {!fetchingCooperativeDetails ? (
          <div className="mt-[12px] w-full">
            <ProgressTrail
              steps={steps}
              currentStep={steps.findIndex((s) => s === step) + 1}
            />
          </div>
        ) : undefined}

        {/* Main Body */}
        <div className="mt-[20px] flex-1 flex flex-col w-full">
          {step === "Personal Information" &&
          steps.includes("Personal Information") ? (
            <ProfileInformation
              fetchingMemberProfile={
                fetchingMemberProfile || fetchingCooperativeDetails
              }
              onProceed={async () => {
                if (steps.includes("A little bit more")) {
                  setStep("A little bit more");
                } else {
                  const resp = await joinCooperative({
                    coop: singleCooperative?.data?.id ?? "",
                    form: singleCooperative?.data?.formdetail?.form?.id,
                    formdata: [],
                  });

                  if (resp.data?.success) {
                    if (steps.includes("Membership Fee")) {
                      setStep("Membership Fee");
                    } else {
                      navigate("/app/my-cooperatives");
                    }
                  }
                }
              }}
              isLoading={joiningCooperative}
            />
          ) : undefined}

          {step === "A little bit more" &&
          steps.includes("A little bit more") &&
          cooperativeConfig.hasForm &&
          singleCooperative?.data ? (
            <MembershipForm
              cooperativeDetails={singleCooperative.data}
              onPrevious={() => {
                setStep("Personal Information");
              }}
              onProceed={async (payload) => {
                const resp = await joinCooperative(payload);

                if (resp.data?.success) {
                  if (steps.includes("Membership Fee")) {
                    setStep("Membership Fee");
                  } else {
                    navigate("/app/my-cooperatives");
                  }
                }
              }}
              loading={joiningCooperative}
            />
          ) : undefined}

          {step === "Membership Fee" &&
          steps.includes("Membership Fee") &&
          cooperativeConfig.hasFee &&
          singleCooperative?.data?.id ? (
            <PayMembershipFee cooperativeId={singleCooperative.data.id} />
          ) : undefined}
        </div>
      </ContainerCard>

      <ConfirmationModal
        isModalOpen={modalOpen && modal === "closeMembershipFormModal"}
        onModalClose={() => {
          handleModalClose();
        }}
        title="Close Form?"
        subtitle={
          <>
            You are about to close{" "}
            <span className="font-medium text-pry-green-200">
              {singleCooperative?.data?.CoopName}
            </span>
            's membership form. This may require you to fill in your details
            again later. Are you sure you want to exit?
          </>
        }
        onProceed={() => {
          navigate("/app/overview");
        }}
        proceedButtonText="Close Form"
        proceedButtonVariant="danger"
        cancelButtonText="Cancel"
        cancelButtonVariant="secondary"
      />
    </>
  );
};
